import { randomUUID as uuid } from 'node:crypto';
import { CURRENCY_TO_SYMBOL, Currency, MARKETPLACE_TO_CURRENCY, type Marketplace } from '@amzn/journeys-common';
import { forEach, intersection, isEmpty, omitBy, uniqBy, uniqueId } from 'es-toolkit/compat';
import { isFeatureEnabled } from 'features/flags';
import type { DpAttribute, PermissionsLdapGroups } from 'generated/graphql';
import { useUserData } from 'hooks/use-user-data';
import { jwtDecode } from 'jwt-decode';
import { type MutableRefObject, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import type { DropdownOptions } from './app.types';

export { withinLaunchDateWindow } from './utils/launch-date';

/** Valid stage values */
export type AppStage = 'prod' | 'beta' | 'dev';

const STAGE_KEY = 'Stage';

/**
 * Reads the current stage from localStorage with safe defaulting.
 * Returns 'dev' if the stored value is null or not a valid stage.
 */
export const getStageFromStorage = (): AppStage => {
  const stored = localStorage.getItem(STAGE_KEY);
  if (stored === 'prod' || stored === 'beta') {
    return stored;
  }
  return 'dev';
};

export function getAttributeVal(attributes: DpAttribute[], key: string): string | undefined {
  return attributes.find((attr) => attr.key === key)?.value;
}

export const ID_TOKEN = 'Id-Token';

export const isLaunchPermOn = isFeatureEnabled('LAUNCH_MANAGEMENT');

/*
 * Generates a list of currencies that is convenient for use in UI components.
 * @return Sorted list of currencies as strings
 */
export function getSortedCurrencies(): Currency[] {
  const list = Object.keys(Currency) as Currency[];
  return list.sort();
}

/**
 * Helper function that produces a list of drop-down options to display
 * @param displaySymbols    True to attempt to display the symbol version of the
 *                          currency. False to display the 3-letter ISO code.
 * @param marketplace       (Optional) Target marketplace. If not provided, the
 *                          full set of currencies is shown.
 * @return A list of values and display values
 */
export function getCurrencyOptions(displaySymbols: boolean, marketplace?: Marketplace): DropdownOptions[] {
  return getSortedCurrencies()
    .filter((currency) => {
      if (marketplace && !isEmpty(marketplace) && marketplace in MARKETPLACE_TO_CURRENCY) {
        return MARKETPLACE_TO_CURRENCY[marketplace] === currency;
      }
      return true;
    })
    .map((currency) => ({
      display: displaySymbols ? CURRENCY_TO_SYMBOL[currency] : currency,
      value: currency,
    }));
}

export function getUserLdapGroups() {
  const token = localStorage.getItem(ID_TOKEN);
  if (!token) return [];
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  const claims = (jwtDecode(token) ?? {}) as any;
  return claims.GROUPS as string[] | undefined;
}

// temporary function for JAS development
export function getLightboxPermissions(): boolean {
  const token = localStorage.getItem(ID_TOKEN);
  if (!token) return false;
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  const claims = (jwtDecode(token) ?? {}) as any;
  const id = claims.UID as string | undefined;
  const groups = claims.GROUPS as string | undefined;
  if (!id) return false;
  const allowedIds: string[] = [
    'svc-mw-850096142096-midway-auth',
    'svc-mw-113962279452-midway-auth',
    'svc-mw-146023164166-midway-auth',
    'svc-mw-116981761347-midway-auth',
  ];
  return groups?.includes('journeys-admin') || allowedIds.includes(id);
}

export function userInMakoMerchTools() {
  const LdapGroups = getUserLdapGroups();
  return LdapGroups?.includes('mako-merch-tools');
}

/**
 * Determine if the passed in object, which could be a promise or an observable, is a promise.
 * @param obj
 */
export function isPromise<GraphQLResult>(obj: object): obj is Promise<GraphQLResult> {
  return 'then' in obj;
}

/**
 * await on this function to sleep for a time period
 * @param ms
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, ms);
  });
}

/**
 * Make sure the result of a graphQL operation is a promise. If not, throw an error
 * @param graphQlResult
 */
export function ensurePromise<T>(graphQlResult: object): Promise<T> {
  if (isPromise<T>(graphQlResult)) {
    return graphQlResult;
  }
  throw Error(
    'A graphql operation resulted in an Observable instead of the expected Promise. ' +
      'This is probably due to accidentally performing a subscription instead of a mutation or query.',
  );
}

export function namespaceEventType<T extends string>(namespace: string, type: T): T {
  return `${namespace}/${type}` as T;
}

/**
 * Takes in an email. If an '@' is found, return everything before it. Otherwise return the string as it was.
 * @param email
 * @returns {*}
 */
export function extractAliasFromEmail(email: string): string {
  if (!email) return email;
  const indexOfAt = email.indexOf('@');
  if (indexOfAt > 0) {
    return email.substring(0, indexOfAt);
  }
  return email;
}

/**
 * Wrap a Web Component in props and event handlers.
 * @param ref A ref on the Web Component's tag
 * @param props The props you want to set on the component
 * @param events Names of custom you want to listen to.
 */
export function useWebComponent<P extends object>({
  ref,
  props,
  events = [],
}: {
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  ref: MutableRefObject<any>;
  props: P;
  events?: Array<keyof P>;
}) {
  useLayoutEffect(() => {
    const webComponent = ref.current;
    if (!webComponent) {
      return;
    }

    // set all the defined props passed in
    forEach(props, (val, propName) => {
      if (val !== undefined) {
        webComponent[propName] = props[propName as keyof P];
      }
    });

    const eventHandlers = events.map((evtName) => [
      evtName,
      // biome-ignore lint/suspicious/noExplicitAny: legacy code
      (evt: any) => {
        const callback = props[evtName];
        if (callback && typeof callback === 'function') {
          return callback(...evt.detail);
        }
      },
    ]);

    eventHandlers.forEach(([evtName, handler]) => {
      webComponent.addEventListener(evtName, handler);
    });

    return () => {
      eventHandlers.forEach(([evtName, handler]) => {
        webComponent.removeEventListener(evtName, handler);
      });
    };
  });
}

/**
 * Show an alert on refresh asking the user if they're sure they want to reload the page.
 * Only show this if shouldPreventUnload is true.
 * @param shouldPreventUnload
 */
export function useWindowUnloadGuard(shouldPreventUnload: boolean) {
  const beforeUnload = useCallback((ev: BeforeUnloadEvent) => {
    ev.preventDefault();
    ev.returnValue = '';
  }, []);

  useEffect(() => {
    if (shouldPreventUnload) {
      window.addEventListener('beforeunload', beforeUnload);
    } else {
      window.removeEventListener('beforeunload', beforeUnload);
    }

    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [
    beforeUnload,
    shouldPreventUnload,
  ]);
}

/**
 * Case-insensitive check whether two arrays of strings have elements in common with each other
 * @param groups1
 * @param groups2
 */
export function someCommon(groups1: string[], groups2: string[]): boolean {
  return (
    intersection(
      groups1.map((g) => g.toLowerCase()),
      groups2.map((g) => g.toLowerCase()),
    ).length > 0
  );
}

/**
 * Helper function that filters objects in a list that share a common key-value pair.
 * If the key does not exist, the element is also filtered from the list.
 * @param list List to filter on
 * @param key Key that has a corresponding value to uniquely filter on
 * @return A list of filtered elements.
 */
export function filterUniqueByValue<
  V,
  E extends {
    [key: string]: V;
  },
>(list: Array<E>, key: string): Array<E> {
  return uniqBy(
    list.filter((elem) => key in elem),
    (elem) => elem[key],
  );
}

// biome-ignore lint/style/noEnum: legacy code
export enum DpbIntegration {
  campaigns = 'campaigns',
  attributes = 'attributes',
  slates = 'slates',
  pricing = 'pricing',
}

/**
 * Check if the user has publish permissions for the type of integration and Detail Page
 * @param integration
 * @param dpGroups
 */
//TODO update the UI piece here to check if the user has permission in launch if there is a launch.
export function useUserHasPublishPermissions(integration: DpbIntegration, dpGroups?: PermissionsLdapGroups | null): boolean {
  const { attributesAndPricing = [], campaignsAndSlates = [] } = dpGroups ?? {
    attributesAndPricing: [],
    campaignsAndSlates: [],
  };
  const { alias, groups } = useUserData();
  // return true for integration-test account
  if (alias === 'journeysdpb-dev') {
    return true;
  }
  const userGroups = groups ?? [];

  switch (integration) {
    case DpbIntegration.attributes:
    case DpbIntegration.pricing:
      return someCommon(userGroups, attributesAndPricing);
    case DpbIntegration.campaigns:
    case DpbIntegration.slates:
      return someCommon(userGroups, campaignsAndSlates);
    default:
      return false;
  }
}

/**
 * Returns true if the UI is being hosted on the prod domain.
 *
 * @returns {boolean}
 */
export function isOnProd(): boolean {
  return (
    window.location && (window.location.hostname === 'journeys.a2z.com' || window.location.hostname === 'detail-page.journeys.a2z.com')
  );
}

/**
 * Returns true if the UI is being hosted on the beta domain.
 *
 * @returns {boolean}
 */
export function isOnBeta(): boolean {
  return (
    window.location &&
    (window.location.hostname === 'beta.journeys.a2z.com' || window.location.hostname === 'beta.detail-page.journeys.a2z.com')
  );
}

/**
 * Returns true if the UI is being hosted on localhost.
 *
 * @returns {boolean}
 */
export function isOnLocalhost(): boolean {
  return window.location && window.location.hostname === 'localhost';
}

/**
 * Utility function to create a unique id suitable for an HTML element.
 *
 * @param prefix The prefix for the unique id.
 * @param suffix An optional suffix for the id. If not provided, an empty string is used.
 * @param id An optional id. If this is not provided a unique id will be generated. This is the only unique portion of
 *           the id. If providing your own id, ensure it will be unique for the page.
 * @returns a string in the format, "prefix-id-suffix".
 */
export function createUniqueElementId(prefix: string, suffix = '', id: string = uniqueId()) {
  return `${prefix}-${id}-${suffix}`;
}
export const useUniqueElementId = (prefix: string, suffix = '', id: string = uniqueId()) => {
  const elementId = useMemo(
    () => createUniqueElementId(prefix, suffix, id),
    [
      id,
      prefix,
      suffix,
    ],
  );
  return elementId;
};

/**
 * Make TypeScript throw a compile-time error if the given x is not narrowed down to nothing.
 * This is useful for when you want to enforce switch statements to be exhaustive.
 * @param _x
 */
export function assertUnreachable(_x: never): never {
  throw new Error('Unreachable statement reached');
}

/**
 * Custom function that should be used within a useEffect call to reset the state of a form inside
 * and perform a pre-ResetFunction.
 *
 * Check CreateLaunchModal.tsx line ~84 to check usage of this function
 *
 *
 * @param formElementId - id of the form element on the page.
 * @param preResetFunction - function or null/undefined for anything that needs to be executed before resetting the form state.
 * @param dependentVariable - variable useEffect will watch for change
 * @constructor
 */

export function useResetFormOnChange(formElementId: string, preResetFunction: () => unknown, dependentVariable: unknown): void {
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  useEffect(() => {
    // biome-ignore lint/nursery/noUnnecessaryConditions: legacy code
    if (preResetFunction) {
      preResetFunction();
    }
    const form = document.getElementById(formElementId) as HTMLFormElement;
    if (form) {
      form.dispatchEvent(
        new Event('reset', {
          cancelable: true,
        }),
      );
    }
  }, [
    dependentVariable,
  ]);
}

export const generateId = () => {
  const base64data = window.btoa(uuid());
  return base64data.substr(0, 12);
};

/**
 * Omit any null or undefined values from obj. Does not recurse into nested objects.
 */
export function omitNullish<T extends object>(obj: T): T {
  return omitBy(obj, isNullish) as T;
}

/**
 * Is a value null or undefined
 */
export function isNullish(val: unknown | null | undefined): val is null | undefined {
  return val === null || val === undefined;
}

/**
 * Replaces Zero-width unicode characters such as uFEFF, u200B.
 * Additionally Replaces Unicode newline characters from a string. Subset of newline character codes taken from
 * https://en.wikipedia.org/wiki/Newline#Unicode. This does not replace all newline characters as many of them
 * are desirable. This does not modify the string provided.
 *
 */
export function replaceUnicodeZeroWidthCharacters(originalString: string, replace = '') {
  if (isEmpty(originalString)) return originalString;
  return originalString.replace(/\uFEFF|\u200B|\u2028|\u2029|\u0085/g, replace);
}
